import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, studentApi, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { LastActivityBadge } from '@/components/LastActivityBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ImportButton from '@/components/ImportButton';
import ReminderModal from '@/components/ReminderModal';
import { getCourseColor, COURSE_COLORS } from '@/components/UpgradeModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { ClickToCall, CallHistory } from '@/components/ClickToCall';
import { CallRecordings } from '@/components/CallRecordings';
import CallAnalyticsWidget from '@/components/CallAnalyticsWidget';
import { TransactionHistory } from '@/components/TransactionHistory';
import { MentionTextarea, RenderNoteBody } from '@/components/MentionTextarea';
import { PeriodFilter } from '@/components/PeriodFilter';
import { EnrollmentMonthFilter } from '@/components/EnrollmentMonthFilter';
import { Pagination } from '@/components/Pagination';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Search,
    Phone,
    Mail,
    User,
    GraduationCap,
    MessageSquare,
    TrendingUp,
    CheckCircle,
    DollarSign,
    MoreVertical,
    Bell,
    PhoneCall,
    GripVertical,
    RefreshCw,
    AlertTriangle,
    PhoneOff,
    Star,
    Shield,
    Users,
    LayoutGrid,
    List,
    Send,
    FileText,
    Loader2,
    Clock,
} from 'lucide-react';
import logger from '@/lib/logger';

const MENTOR_STAGES = [
    { id: 'new_student', label: 'New Student', color: 'bg-blue-500', icon: User },
    { id: 'satisfactory_call', label: 'Satisfactory Call', color: 'bg-purple-500', icon: MessageSquare },
    { id: 'did_meeting', label: 'Did Meeting', color: 'bg-indigo-500', icon: Users },
    { id: 'pitched_for_upgrade', label: 'Pitched Upgrade', color: 'bg-cyan-500', icon: TrendingUp },
    { id: 'upgraded', label: 'Upgraded', color: 'bg-teal-500', icon: DollarSign },
];

const MENTOR_COLOR_TAG_STYLES = {
    handle_with_care: { label: 'Handle With Care', color: 'bg-amber-100 border-amber-400 text-amber-800', dot: 'bg-amber-500', icon: AlertTriangle },
    do_not_disturb: { label: 'Do Not Disturb', color: 'bg-red-100 border-red-400 text-red-800', dot: 'bg-red-500', icon: PhoneOff },
    vip: { label: 'VIP Client', color: 'bg-yellow-100 border-yellow-500 text-yellow-800', dot: 'bg-yellow-500', icon: Star },
    priority: { label: 'Priority', color: 'bg-purple-100 border-purple-400 text-purple-800', dot: 'bg-purple-500', icon: Shield },
    follow_up: { label: 'Follow Up', color: 'bg-blue-100 border-blue-400 text-blue-800', dot: 'bg-blue-500', icon: Bell },
};

const StudentCard = ({ student, onView, onSetReminder, isDragging, isSuperAdmin, mentorAgents, onReassign, bdAgents, onReassignBD }) => {
    const hasReminder = student.reminder_date && !student.reminder_completed;
    const courseLevel = student.course_level || '';
    const courseName = courseLevel || student.current_course_name || student.package_bought;
    const courseColors = getCourseColor(courseName);
    const hasCourse = !!courseName;
    const colorTag = student.color_tag ? MENTOR_COLOR_TAG_STYLES[student.color_tag] : null;
    const isInClosed = student.mentor_stage === 'redeposits';
    const hasRedeposited = (student.redeposit_count || 0) > 0;
    const isUpgradedStudent = student.mentor_stage === 'upgraded';

    const isShadow = student.is_shadow;
    return (
        <div
            className={`kanban-card stage-${student.mentor_stage} animate-fade-in cursor-pointer ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''} ${isShadow ? 'bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-400 dark:border-amber-600 ring-1 ring-amber-300/30 opacity-80' : isInClosed ? 'bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-300/30' : colorTag ? `${colorTag.color.split(' ')[0]} border-2 ${colorTag.color.split(' ')[1]}` : hasCourse ? `${courseColors.border} border-l-4` : ''}`}
            onClick={() => !isDragging && !isShadow && onView(student)}
            data-testid={`mentor-card-${student.id}`}
        >
            {isShadow && (
                <div className="-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md bg-amber-500 text-white">
                    <DollarSign className="h-3 w-3" /> Redeposit — {student.date ? new Date(student.date).toLocaleDateString() : ''} — AED {(student.amount || 0).toLocaleString()}
                </div>
            )}
            {!isShadow && isInClosed && (
                <div className="-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md bg-emerald-500 text-white">
                    <DollarSign className="h-3 w-3" /> Awaiting Deposit Recording
                </div>
            )}
            {!isInClosed && colorTag && (
                <div className={`-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md ${colorTag.color}`}>
                    <colorTag.icon className="h-3 w-3" />
                    {colorTag.label}
                </div>
            )}
            <div className="flex items-start justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                    <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                        <GripVertical className="h-3.5 w-3.5" />
                    </div>
                    <div className={`w-6 h-6 rounded-full ${hasCourse ? courseColors.bg + ' ' + courseColors.text : 'bg-orange-600 text-white'} flex items-center justify-center text-xs font-medium`}>
                        {student.full_name?.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <p className="font-medium text-xs truncate">{student.full_name}</p>
                            {hasRedeposited && (
                                <Badge className="bg-emerald-500 text-white text-[9px] px-0.5 py-0 leading-tight">
                                    x{student.redeposit_count}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(student); }}>
                            View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSetReminder(student); }}>
                            <Bell className="h-4 w-4 mr-2" />
                            Set Reminder
                        </DropdownMenuItem>
                        {isSuperAdmin && mentorAgents && mentorAgents.length > 0 && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs text-muted-foreground">Reassign Mentor:</DropdownMenuLabel>
                                {mentorAgents.filter(m => m.id !== student.mentor_id).map(m => (
                                    <DropdownMenuItem 
                                        key={m.id}
                                        onClick={(e) => { e.stopPropagation(); onReassign(student.id, m.id); }}
                                        data-testid={`reassign-${student.id}-to-${m.id}`}
                                    >
                                        <RefreshCw className="h-3 w-3 mr-2" />
                                        {m.full_name}
                                    </DropdownMenuItem>
                                ))}
                            </>
                        )}
                        {isSuperAdmin && bdAgents && bdAgents.length > 0 && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs text-muted-foreground">Change BD Agent:</DropdownMenuLabel>
                                {bdAgents.filter(a => a.id !== student.bd_agent_id).map(a => (
                                    <DropdownMenuItem
                                        key={a.id}
                                        onClick={(e) => { e.stopPropagation(); onReassignBD(student.id, a.id); }}
                                        data-testid={`reassign-bd-${student.id}-to-${a.id}`}
                                    >
                                        <User className="h-3 w-3 mr-2 text-sky-500" />
                                        {a.full_name}
                                    </DropdownMenuItem>
                                ))}
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            
            <div className="space-y-1 text-xs">
                {isShadow ? (
                    <div className="flex items-center gap-1 text-muted-foreground">
                        <DollarSign className="h-3 w-3" />
                        <span className="font-mono">AED {(student.amount || 0).toLocaleString()}</span>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span className="font-mono text-[11px] flex-1 truncate">{student.phone}</span>
                            <ClickToCall 
                                phoneNumber={student.phone} 
                                contactId={student.id} 
                                contactName={student.full_name}
                                size="sm"
                                className="h-5 w-5"
                            />
                        </div>
                        {courseName && (
                            <span className={`text-[10px] font-medium ${courseColors.text}`}>{courseColors.label || courseName}</span>
                        )}
                    </>
                )}
            </div>
            
            <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border text-[10px]">
                <div className="flex items-center gap-1 flex-wrap">
                    {student.ltv_total > 0 && (
                        <span className="font-mono text-sky-600 dark:text-sky-400" data-testid={`mentor-ltv-${student.id}`}>
                            AED {student.ltv_total?.toLocaleString()}
                        </span>
                    )}
                    {student.onboarding_complete && <span className="text-emerald-500 font-medium">Onboarded</span>}
                    {student.upgrade_eligible && !isUpgradedStudent && <span className="text-yellow-500 font-medium">Upgrade</span>}
                    {hasReminder && <Bell className="h-3 w-3 text-amber-500" />}
                </div>
                <span className="text-muted-foreground">{student.classes_attended || 0} cls</span>
            </div>
            {(student.mentor_name || student.bd_agent_name) && (
                <div className="flex items-center gap-1.5 mt-1 text-[9px] text-muted-foreground">
                    {student.mentor_name && <span className="text-violet-500">{student.mentor_name.split(' ')[0]}</span>}
                    {student.bd_agent_name && <span className="text-sky-500">BD: {student.bd_agent_name.split(' ')[0]}</span>}
                </div>
            )}
            <div className="mt-1" data-testid={`last-activity-${student.id}`}>
                <LastActivityBadge at={student.last_activity} by={student.last_activity_by} compact />
            </div>
        </div>
    );
};

// Sortable wrapper for StudentCard
const SortableStudentCard = ({ student, onView, onSetReminder, isSuperAdmin, mentorAgents, onReassign, bdAgents, onReassignBD }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: student.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <StudentCard
                student={student}
                onView={onView}
                onSetReminder={onSetReminder}
                isDragging={isDragging}
                isSuperAdmin={isSuperAdmin}
                mentorAgents={mentorAgents}
                onReassign={onReassign}
                bdAgents={bdAgents}
                onReassignBD={onReassignBD}
            />
        </div>
    );
};

const MENTOR_COLUMN_PAGE_SIZE = 50;

const KanbanColumn = ({ stage, baseParams, shadowCards, onView, onSetReminder, onHeaderClick, headerExtra, isSuperAdmin, mentorAgents, onReassign, bdAgents, onReassignBD }) => {
    const [colPage, setColPage] = React.useState(1);
    const [colStudents, setColStudents] = React.useState([]);
    const [colTotal, setColTotal] = React.useState(0);
    const [colLoading, setColLoading] = React.useState(true);
    const StageIcon = stage.icon;

    React.useEffect(() => {
        let cancelled = false;
        const fetchColumnData = async () => {
            setColLoading(true);
            try {
                const res = await apiClient.get('/students', {
                    params: { ...baseParams, mentor_stage: stage.id, activated_only: true, page: colPage, page_size: MENTOR_COLUMN_PAGE_SIZE }
                });
                if (cancelled) return;
                const data = res.data;
                const items = (data?.items || (Array.isArray(data) ? data : [])).filter(s => s.mentor_id || s.mentor_stage);
                setColStudents(items);
                setColTotal(data?.total || items.length);
            } catch {
                if (!cancelled) setColStudents([]);
            }
            if (!cancelled) setColLoading(false);
        };
        fetchColumnData();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colPage, baseParams?.mentor_id, baseParams?.date_from, baseParams?.date_to, baseParams?.search, stage.id]);

    // Shadow cards removed — redeposits column no longer exists
    const displayStudents = colStudents;

    const totalForDisplay = colTotal;
    const totalColPages = Math.ceil(colTotal / MENTOR_COLUMN_PAGE_SIZE);
    const studentIds = displayStudents.map(s => s.id);

    const { setNodeRef, isOver } = useDroppable({ id: stage.id });
    
    return (
        <div 
            ref={setNodeRef}
            className={`flex flex-col min-w-0 flex-1 bg-muted/50 rounded-xl p-3 ${isOver ? 'ring-2 ring-primary ring-offset-2' : ''}`}
            data-testid={`mentor-column-${stage.id}`}
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 280px)' }}
        >
            <div className={`flex items-center justify-between mb-3 pb-2 border-b border-border ${onHeaderClick ? 'cursor-pointer hover:bg-muted/50 rounded-lg transition-colors' : ''}`} onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (onHeaderClick) onHeaderClick(); }}>
                <div className="flex items-center gap-2">
                    <StageIcon className={`h-4 w-4 ${stage.color.replace('bg-', 'text-')}`} />
                    <h3 className="font-semibold text-sm">{stage.label}</h3>
                </div>
                <div className="flex items-center gap-2">
                    {headerExtra}
                    <Badge variant="secondary" className="text-xs">{totalForDisplay}</Badge>
                </div>
            </div>
            {colLoading ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
                </div>
            ) : (
                <ScrollArea className="flex-1">
                    <SortableContext items={studentIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 min-h-[80px] p-0.5">
                            {displayStudents.map((student) => (
                                <SortableStudentCard
                                    key={student.id}
                                    student={student}
                                    onView={onView}
                                    onSetReminder={onSetReminder}
                                    isSuperAdmin={isSuperAdmin}
                                    mentorAgents={mentorAgents}
                                    onReassign={onReassign}
                                    bdAgents={bdAgents}
                                    onReassignBD={onReassignBD}
                                />
                            ))}
                            {displayStudents.length === 0 && (
                                <div className={`text-center text-muted-foreground py-6 text-xs border-2 border-dashed rounded-lg transition-colors ${isOver ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                                    Drop students here
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </ScrollArea>
            )}
            {totalColPages > 1 && (
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
                    <button onClick={() => setColPage(p => Math.max(1, p - 1))} disabled={colPage <= 1}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`mentor-col-prev-${stage.id}`}>Prev</button>
                    <span className="text-[10px] text-muted-foreground">
                        {(colPage - 1) * MENTOR_COLUMN_PAGE_SIZE + 1}-{Math.min(colPage * MENTOR_COLUMN_PAGE_SIZE, colTotal)} of {colTotal}
                    </span>
                    <button onClick={() => setColPage(p => Math.min(totalColPages, p + 1))} disabled={colPage >= totalColPages}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`mentor-col-next-${stage.id}`}>Next</button>
                </div>
            )}
        </div>
    );
};

const MentorCRMPage = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('my_work'); // 'my_work' | 'team'
    const [showRedepositOnly, setShowRedepositOnly] = useState(false);
    const [teamMentors, setTeamMentors] = useState([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [reminderStudent, setReminderStudent] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [updateData, setUpdateData] = useState({
        mentor_stage: '',
        notes: '',
    });
    const [redepositSummary, setRedepositSummary] = useState(null);
    const [mentorAgentsList, setMentorAgentsList] = useState([]);
    const [bdAgentsList, setBdAgentsList] = useState([]);
    const [filterMentorAgent, setFilterMentorAgent] = useState('all');
    const [showClosingsDialog, setShowClosingsDialog] = useState(false);
    const [monthlyClosings, setMonthlyClosings] = useState(null);
    const [loadingClosings, setLoadingClosings] = useState(false);
    const [mentorPeriodFilter, setMentorPeriodFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [csPartner, setCsPartner] = useState(null);
    const [mentorShadowCards, setMentorShadowCards] = useState([]);

    const [displayMode, setDisplayMode] = useState('kanban'); // 'kanban' | 'table'

    const [studentNotes, setStudentNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [highlightNoteId, setHighlightNoteId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const notesScrollRef = useRef(null);
    const [activityTimeline, setActivityTimeline] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [enrollmentMonth, setEnrollmentMonth] = useState('all');
    const [tableStageFilter, setTableStageFilter] = useState('all');

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        fetchStudents();
        fetchRevenueSummary();
        fetchMentorShadowCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, filterMentorAgent, mentorPeriodFilter, currentPage, pageSize, displayMode, tableStageFilter, enrollmentMonth]);

    // Fetch CS partner info
    useEffect(() => {
        if (user?.id) {
            apiClient.get(`/cs-mentor/partner/${user.id}`)
                .then(res => { if (res.data?.partner) setCsPartner(res.data); })
                .catch(() => {});
        }
    }, [user?.id]);

    const isHeadOrAdmin = ['academic_master', 'master_of_academics', 'master_of_academics_', 'super_admin', 'admin', 'cs_head'].includes(user?.role);
    const isSuperAdmin = user?.role === 'super_admin';

    // Fetch mentor agents for super admin / academic master filtering
    // Include both mentor and master_of_academics roles
    useEffect(() => {
        if (isSuperAdmin || isHeadOrAdmin) {
            Promise.all([
                apiClient.get('/users?role=mentor'),
                apiClient.get('/users?role=master_of_academics'),
                apiClient.get('/users?role=master_of_academics_'),
                apiClient.get('/users?role=academic_master')
            ]).then(([mentorRes, moaRes, moa2Res, amRes]) => {
                const allMentors = [
                    ...(mentorRes.data || []),
                    ...(moaRes.data || []),
                    ...(moa2Res.data || []),
                    ...(amRes.data || [])
                ].filter(u => u.is_active !== false);
                // Remove duplicates by id
                const uniqueMentors = Array.from(new Map(allMentors.map(m => [m.id, m])).values());
                setMentorAgentsList(uniqueMentors);
            }).catch(() => {});
        }
        // Fetch BD agents for super admin reassignment
        if (isSuperAdmin) {
            apiClient.get('/users?role=business_development_manager').then(res => {
                const bdUsers = (res.data || []).filter(u => u.is_active !== false);
                setBdAgentsList(bdUsers);
            }).catch(() => {});
        }
    }, [isSuperAdmin, isHeadOrAdmin]);

    const fetchRevenueSummary = async () => {
        try {
            // Non-super-admin non-head: show only own data
            let params = '';
            if (!isSuperAdmin && !isHeadOrAdmin) {
                params = `?mentor_id=${user?.id}`;
            } else if (filterMentorAgent && filterMentorAgent !== 'all') {
                params = `?mentor_id=${filterMentorAgent}`;
            }
            const response = await apiClient.get(`/mentor/revenue-summary${params}`);
            setRedepositSummary(response.data);
        } catch (error) {
            logger.error('Failed to fetch revenue summary:', error);
            try {
                const fallback = await apiClient.get('/mentor/redeposits/summary');
                setRedepositSummary(fallback.data);
            } catch (e) {
                logger.error('Failed to fetch redeposit summary:', e);
            }
        }
    };

    const fetchMonthlyClosings = async () => {
        setLoadingClosings(true);
        try {
            let params = '';
            if (!isSuperAdmin) {
                params = `?mentor_id=${user?.id}`;
            } else if (filterMentorAgent && filterMentorAgent !== 'all') {
                params = `?mentor_id=${filterMentorAgent}`;
            }
            const response = await apiClient.get(`/mentor/monthly-closings${params}`);
            setMonthlyClosings(response.data);
            setShowClosingsDialog(true);
        } catch (error) {
            toast.error('Failed to load monthly closings');
        } finally {
            setLoadingClosings(false);
        }
    };

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const params = { 
                search: searchTerm || undefined,
                activated_only: true,
                page: currentPage,
                page_size: pageSize,
            };
            if (viewMode === 'my_work' || !isHeadOrAdmin) {
                params.mentor_id = user.id;
            }
            if (filterMentorAgent !== 'all') {
                params.mentor_id = filterMentorAgent;
            }
            // Table view: apply stage filter
            if (displayMode === 'table' && tableStageFilter !== 'all') {
                params.mentor_stage = tableStageFilter;
            }
            // Enrollment month filter
            if (enrollmentMonth && enrollmentMonth !== 'all') {
                const [y, m] = enrollmentMonth.split('-');
                const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
                params.date_from = `${enrollmentMonth}-01`;
                params.date_to = `${enrollmentMonth}-${lastDay}`;
                params.date_field = 'enrolled_at';
            }
            const response = await studentApi.getAll(params);
            const data = response.data;
            const items = data?.items || (Array.isArray(data) ? data : []);
            const mentorStudents = items.filter(s => s.mentor_id || s.mentor_stage);
            setStudents(mentorStudents);
            setTotalRecords(data?.total || mentorStudents.length);
            setTotalPages(data?.total_pages || 0);
            if (viewMode === 'team' && isHeadOrAdmin) {
                const mentorMap = {};
                mentorStudents.forEach(s => {
                    const key = s.mentor_id || 'unassigned';
                    if (!mentorMap[key]) mentorMap[key] = { id: key, name: s.mentor_name || 'Unassigned', count: 0 };
                    mentorMap[key].count++;
                });
                setTeamMentors(Object.values(mentorMap).sort((a, b) => b.count - a.count));
            }
        } catch (error) {
            toast.error('Failed to fetch students');
            logger.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setTimeout(() => { fetchStudents(); fetchMentorShadowCards(); }, 500);
    };

    const fetchMentorShadowCards = async () => {
        try {
            const params = {};
            if (!isSuperAdmin && !isHeadOrAdmin) params.mentor_id = user?.id;
            if (filterMentorAgent && filterMentorAgent !== 'all') params.mentor_id = filterMentorAgent;
            if (mentorPeriodFilter?.date_from) params.date_from = mentorPeriodFilter.date_from;
            if (mentorPeriodFilter?.date_to) params.date_to = mentorPeriodFilter.date_to;
            const res = await apiClient.get('/mentor/redeposit-shadows', { params });
            setMentorShadowCards(res.data?.items || []);
        } catch {
            setMentorShadowCards([]);
        }
    };

    // Base params for ALL columns EXCEPT upgraded — NO date filter applied
    const mentorBaseParams = useMemo(() => {
        const p = {};
        if (viewMode === 'my_work' || !isHeadOrAdmin) p.mentor_id = user?.id;
        if (filterMentorAgent && filterMentorAgent !== 'all') p.mentor_id = filterMentorAgent;
        if (searchTerm) p.search = searchTerm;
        return p;
    }, [viewMode, isHeadOrAdmin, user?.id, filterMentorAgent, searchTerm]);

    // Params for the UPGRADED column — includes date filter (defaults to current month)
    const upgradedBaseParams = useMemo(() => {
        const p = { ...mentorBaseParams };
        if (mentorPeriodFilter?.date_from) {
            p.date_from = mentorPeriodFilter.date_from;
            p.date_to = mentorPeriodFilter.date_to;
        } else {
            // Default to current month
            const now = new Date();
            p.date_from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            p.date_to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        }
        p.date_field = 'last_upgrade_at';
        return p;
    }, [mentorBaseParams, mentorPeriodFilter]);

    const handleViewStudent = (student, opts = {}) => {
        setSelectedStudent(student);
        setUpdateData({
            mentor_stage: student.mentor_stage || 'new_student',
            notes: '',
        });
        setShowDetailModal(true);
        setNewNote('');
        if (opts.highlightNoteId) {
            setHighlightNoteId(opts.highlightNoteId);
        } else {
            setHighlightNoteId(null);
        }
        fetchStudentNotes(student.id);
        fetchActivityTimeline(student.id);
    };

    // Deep-link: open student + highlight note when arriving from a mention notification
    useEffect(() => {
        const studentId = searchParams.get('student_id');
        const noteId = searchParams.get('note_id');
        if (!studentId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiClient.get(`/students/${studentId}`);
                if (!cancelled && res?.data) {
                    handleViewStudent(res.data, { highlightNoteId: noteId });
                    // Clear the URL params so refresh doesn't re-open
                    const clean = new URLSearchParams(searchParams);
                    clean.delete('student_id');
                    clean.delete('note_id');
                    setSearchParams(clean, { replace: true });
                }
            } catch (e) {
                toast.error('Could not open referenced student');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Scroll to & pulse the highlighted note once the notes load
    useEffect(() => {
        if (!highlightNoteId || !studentNotes.length) return;
        const el = document.querySelector(`[data-note-id="${highlightNoteId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('note-pulse-highlight');
            const t = setTimeout(() => {
                el.classList.remove('note-pulse-highlight');
                setHighlightNoteId(null);
            }, 3200);
            return () => clearTimeout(t);
        }
    }, [highlightNoteId, studentNotes]);

    const fetchStudentNotes = async (studentId) => {
        try {
            const res = await apiClient.get(`/students/${studentId}/notes`);
            setStudentNotes(Array.isArray(res.data) ? res.data : []);
        } catch { setStudentNotes([]); }
    };

    const fetchActivityTimeline = async (studentId) => {
        setActivityLoading(true);
        try {
            const res = await apiClient.get(`/students/${studentId}/activity-timeline`);
            setActivityTimeline(Array.isArray(res.data) ? res.data : []);
        } catch { setActivityTimeline([]); }
        setActivityLoading(false);
    };

    const ROLE_LABELS_M = {
        cs_agent: 'CS', cs_head: 'CS Head', mentor: 'Mentor', master_of_academics: 'Mentor',
        business_development: 'BD', bd_executive: 'BD', super_admin: 'Admin', admin: 'Admin',
        sales_executive: 'Sales', team_leader: 'TL',
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !selectedStudent) return;
        setSavingNote(true);
        try {
            await apiClient.post(`/students/${selectedStudent.id}/notes`, { text: newNote, type: 'session_note' });
            setNewNote('');
            toast.success('Note saved');
            fetchStudentNotes(selectedStudent.id);
        } catch { toast.error('Failed to save note'); }
        setSavingNote(false);
    };

    const handleSetReminder = (student) => {
        setReminderStudent(student);
        setShowReminderModal(true);
    };

    const handleReminderSuccess = () => {
        setShowReminderModal(false);
        setReminderStudent(null);
        fetchStudents();
    };

    const handleReassignMentor = async (studentId, newMentorId) => {
        try {
            await apiClient.post(`/students/${studentId}/reassign-mentor?new_mentor_id=${newMentorId}`);
            toast.success('Student reassigned successfully');
            fetchStudents();
            fetchRevenueSummary();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reassign student');
        }
    };

    const handleReassignBD = async (studentId, newBdAgentId) => {
        try {
            await apiClient.post(`/bd/students/${studentId}/reassign?new_bd_agent_id=${newBdAgentId}`);
            toast.success('BD agent changed successfully');
            fetchStudents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change BD agent');
        }
    };

    // Drag and drop handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeStudentId = active.id;
        const activeStudent = students.find(s => s.id === activeStudentId);
        
        if (!activeStudent) return;

        // Determine target stage
        let targetStage = null;
        
        // Check if dropped over a column (stage id)
        const isColumnDrop = MENTOR_STAGES.some(s => s.id === over.id);
        if (isColumnDrop) {
            targetStage = over.id;
        } else {
            // Dropped over another student - get that student's mentor_stage
            const overStudent = students.find(s => s.id === over.id);
            if (overStudent) {
                targetStage = overStudent.mentor_stage;
            }
        }

        // If dropped in a different stage, update the student
        if (targetStage && targetStage !== activeStudent.mentor_stage) {
            try {
                await studentApi.update(activeStudentId, { mentor_stage: targetStage });
                if (targetStage === 'closed') {
                    toast.success('Deposit recorded — student cycled back to New Students');
                } else {
                    toast.success(`Student moved to ${MENTOR_STAGES.find(s => s.id === targetStage)?.label}`);
                }
                fetchStudents();
            } catch (error) {
                toast.error('Failed to move student');
                logger.error(error);
            }
        }
    };

    const handleUpdateStudent = async () => {
        if (!selectedStudent) return;
        
        try {
            await studentApi.update(selectedStudent.id, updateData);
            if (updateData.mentor_stage === 'closed') {
                toast.success('Deposit recorded — student cycled back to New Students');
            } else {
                toast.success('Student updated successfully');
            }
            setShowDetailModal(false);
            fetchStudents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update student');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-AE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    return (
        <div className="space-y-6" data-testid="mentor-crm-page">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Mentor CRM</h1>
                        <p className="text-muted-foreground">Manage student mentorship and upgrade pipeline</p>
                    </div>
                    {csPartner?.partner && (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800" data-testid="cs-partner-badge">
                            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">CS Partner:</span>
                            <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">{csPartner.partner.name}</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="pl-9 w-64"
                            data-testid="search-mentor-students"
                        />
                    </div>
                    {(isSuperAdmin || isHeadOrAdmin) && mentorAgentsList.length > 0 && (
                        <Select value={filterMentorAgent} onValueChange={setFilterMentorAgent} data-testid="filter-mentor-agent">
                            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="filter-mentor-agent-trigger">
                                <SelectValue placeholder="All Mentors" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Mentors</SelectItem>
                                {mentorAgentsList.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <PeriodFilter
                        dateFields={[{ value: 'deposit_date', label: 'Deposit Date' }]}
                        onChange={setMentorPeriodFilter}
                    />
                    <EnrollmentMonthFilter value={enrollmentMonth} onChange={(v) => { setEnrollmentMonth(v); setCurrentPage(1); }} />
                    <Button
                        variant={showRedepositOnly ? 'default' : 'outline'}
                        size="sm"
                        className="h-8 text-xs gap-1"
                        onClick={() => setShowRedepositOnly(!showRedepositOnly)}
                        data-testid="mentor-redeposit-filter"
                    >
                        <DollarSign className="h-3 w-3" />
                        Redeposit Students
                    </Button>
                    {['super_admin', 'admin', 'academic_master'].includes(user?.role) && (
                        <>
                            <ImportButton templateType="students_mentor" title="Import Students" onSuccess={fetchStudents} />
                            <ImportButton 
                                templateType="mentor_redeposits" 
                                title="Import Redeposits" 
                                onSuccess={() => { fetchStudents(); fetchRevenueSummary(); }} 
                            />
                        </>
                    )}
                    {['super_admin', 'admin', 'finance'].includes(user?.role) && (
                        <ImportButton 
                            templateType="mentor_withdrawals" 
                            title="Import Withdrawals" 
                            onSuccess={() => { fetchStudents(); fetchRevenueSummary(); }} 
                        />
                    )}
                </div>
            </div>

            {/* Call Stats */}
            <CallAnalyticsWidget compact />

            {/* View Mode Toggle for Academic Master / Admin */}
            {isHeadOrAdmin && (
                <div className="flex items-center gap-3" data-testid="mentor-view-toggle">
                    <div className="inline-flex rounded-lg border p-1 bg-muted/30">
                        <button
                            onClick={() => setViewMode('my_work')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'my_work' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="mentor-view-my-work"
                        >
                            My Students
                        </button>
                        <button
                            onClick={() => setViewMode('team')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'team' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="mentor-view-team"
                        >
                            Team Overview
                        </button>
                    </div>
                    {viewMode === 'team' && teamMentors.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {teamMentors.map(mentor => (
                                <Badge key={mentor.id} variant="secondary" className="text-xs">
                                    {mentor.name}: {mentor.count}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Kanban/Table Display Toggle */}
            <div className="flex items-center justify-between">
                <div className="inline-flex rounded-lg border p-1 bg-muted/30" data-testid="mentor-display-toggle">
                    <button
                        onClick={() => setDisplayMode('kanban')}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'kanban' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="mentor-kanban-toggle"
                    >
                        <LayoutGrid className="h-3.5 w-3.5" />
                        Kanban
                    </button>
                    <button
                        onClick={() => { setDisplayMode('table'); setCurrentPage(1); }}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'table' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="mentor-table-toggle"
                    >
                        <List className="h-3.5 w-3.5" />
                        Table
                    </button>
                </div>
                {displayMode === 'table' && (
                    <Select value={tableStageFilter} onValueChange={(v) => { setTableStageFilter(v); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="mentor-table-stage-filter">
                            <SelectValue placeholder="Filter by Stage" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Stages</SelectItem>
                            {MENTOR_STAGES.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                    <span className="flex items-center gap-2">
                                        <span className={`inline-block w-2 h-2 rounded-full ${s.color}`} />
                                        {s.label}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Kanban / Table Board */}
            {loading ? (
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : displayMode === 'table' ? (
                <div className="border rounded-lg overflow-hidden" data-testid="mentor-table-view">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead className="w-12">#</TableHead>
                                <TableHead>Student</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Stage</TableHead>
                                <TableHead>Course</TableHead>
                                <TableHead className="text-right">Enrollment</TableHead>
                                <TableHead>Mentor</TableHead>
                                <TableHead>CS Agent</TableHead>
                                <TableHead>BD Agent</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {students.map((s, idx) => {
                                const stageObj = MENTOR_STAGES.find(st => st.id === s.mentor_stage);
                                return (
                                    <TableRow key={s.id} className="cursor-pointer hover:bg-muted/30" onClick={() => handleViewStudent(s)} data-testid={`mentor-table-row-${s.id}`}>
                                        <TableCell className="text-muted-foreground">{(currentPage - 1) * pageSize + idx + 1}</TableCell>
                                        <TableCell className="font-medium">{s.full_name}</TableCell>
                                        <TableCell className="text-muted-foreground">{s.phone}</TableCell>
                                        <TableCell>
                                            {stageObj ? (
                                                <Badge className={`${stageObj.color} text-white text-[10px]`}>{stageObj.label}</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px]">{s.mentor_stage || s.stage}</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-xs">{s.course_level || s.current_course_name || s.package_bought || '—'}</TableCell>
                                        <TableCell className="text-right font-mono text-xs">AED {(s.enrollment_amount || 0).toLocaleString()}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{s.mentor_name || '—'}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{s.cs_agent_name || '—'}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{s.bd_agent_name || '—'}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {students.length === 0 && (
                                <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No students found</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="flex gap-4 overflow-x-auto pb-4">
                        {MENTOR_STAGES.map((stage) => (
                            <KanbanColumn
                                key={stage.id}
                                stage={stage}
                                baseParams={stage.id === 'upgraded' ? upgradedBaseParams : mentorBaseParams}
                                shadowCards={[]}
                                onView={handleViewStudent}
                                onSetReminder={handleSetReminder}
                                onHeaderClick={undefined}
                                headerExtra={undefined}
                                isSuperAdmin={isSuperAdmin}
                                mentorAgents={mentorAgentsList}
                                onReassign={handleReassignMentor}
                                bdAgents={bdAgentsList}
                                onReassignBD={handleReassignBD}
                            />
                        ))}
                    </div>
                    <DragOverlay>
                        {activeId ? (
                            <StudentCard
                                student={students.find(s => s.id === activeId)}
                                onView={() => {}}
                                onSetReminder={() => {}}
                                isDragging={true}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Pagination */}
            {!loading && students.length > 0 && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    total={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* Student Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl max-h-[90vh]">
                    <DialogHeader className="sr-only"><DialogTitle>{selectedStudent?.full_name || 'Student Details'}</DialogTitle></DialogHeader>
                    {selectedStudent && (
                        <>
                            {/* Compact Header - Always Visible */}
                            <div className="flex items-center gap-3 pb-3 border-b">
                                <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {selectedStudent.full_name?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold truncate">{selectedStudent.full_name}</h3>
                                        <Badge className={`text-[10px] ${MENTOR_STAGES.find(s => s.id === selectedStudent.mentor_stage)?.color || 'bg-blue-500'}`}>
                                            {MENTOR_STAGES.find(s => s.id === selectedStudent.mentor_stage)?.label || 'New Student'}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedStudent.phone}</span>
                                        {selectedStudent.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedStudent.email}</span>}
                                        <span>{selectedStudent.package_bought || 'N/A'}</span>
                                    </div>
                                </div>
                                <ClickToCall
                                    phoneNumber={selectedStudent.phone}
                                    contactId={selectedStudent.id}
                                    contactName={selectedStudent.full_name}
                                    variant="outline"
                                    size="sm"
                                    showLabel={false}
                                />
                                <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setReminderStudent(selectedStudent); setShowReminderModal(true); }} data-testid="mentor-set-reminder-btn">
                                    <Bell className="h-3 w-3" /> Reminder
                                </Button>
                            </div>

                            {/* Tabbed Content */}
                            <Tabs defaultValue="notes" className="mt-2">
                                <TabsList className="grid w-full grid-cols-6 h-8">
                                    <TabsTrigger value="info" className="text-xs" data-testid="mentor-tab-info">Info</TabsTrigger>
                                    <TabsTrigger value="notes" className="text-xs" data-testid="mentor-tab-notes">
                                        <FileText className="h-3 w-3 mr-1" />Notes
                                    </TabsTrigger>
                                    <TabsTrigger value="activity" className="text-xs" data-testid="mentor-tab-activity">Activity</TabsTrigger>
                                    <TabsTrigger value="transactions" className="text-xs" data-testid="mentor-tab-transactions">
                                        <DollarSign className="h-3 w-3 mr-1" />Txns
                                    </TabsTrigger>
                                    <TabsTrigger value="calls" className="text-xs" data-testid="mentor-tab-calls">
                                        <PhoneCall className="h-3 w-3 mr-1" />Calls
                                    </TabsTrigger>
                                    <TabsTrigger value="update" className="text-xs" data-testid="mentor-tab-update">Update</TabsTrigger>
                                </TabsList>

                                {/* INFO TAB */}
                                <TabsContent value="info" className="mt-3 space-y-4" data-testid="mentor-tab-content-info">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-muted-foreground">Package:</span><span className="ml-2">{selectedStudent.package_bought || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Trading Level:</span><span className="ml-2">{selectedStudent.trading_level || 'Beginner'}</span></div>
                                        <div><span className="text-muted-foreground">Classes:</span><span className="ml-2 font-mono">{selectedStudent.classes_attended || 0}</span></div>
                                        <div><span className="text-muted-foreground">Language:</span><span className="ml-2">{selectedStudent.preferred_language || 'English'}</span></div>
                                        <div><span className="text-muted-foreground">Learning Goals:</span><span className="ml-2">{selectedStudent.learning_goals || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Enrolled:</span><span className="ml-2">{formatDate(selectedStudent.created_at)}</span></div>
                                    </div>
                                    {/* Color Tags */}
                                    <div className="flex items-center gap-2 flex-wrap" data-testid="mentor-color-tag-picker">
                                        <span className="text-xs text-muted-foreground mr-1">Tag:</span>
                                        {Object.entries(MENTOR_COLOR_TAG_STYLES).map(([id, tag]) => (
                                            <button key={id}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-all ${selectedStudent.color_tag === id ? tag.color + ' ring-2 ring-offset-1 ring-current font-bold' : 'border-muted text-muted-foreground hover:border-foreground'}`}
                                                onClick={async () => {
                                                    const newTag = selectedStudent.color_tag === id ? null : id;
                                                    try {
                                                        await apiClient.patch(`/students/${selectedStudent.id}/color-tag`, { color_tag: newTag });
                                                        setSelectedStudent(prev => ({ ...prev, color_tag: newTag }));
                                                        setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, color_tag: newTag } : s));
                                                    } catch { toast.error('Failed to update tag'); }
                                                }}
                                                data-testid={`mentor-color-tag-btn-${id}`}>
                                                <div className={`h-2 w-2 rounded-full ${tag.dot}`} />{tag.label}
                                            </button>
                                        ))}
                                    </div>
                                    {/* BD Agent — super admin can change */}
                                    {isSuperAdmin && (
                                        <div className="pt-3 border-t space-y-2" data-testid="bd-agent-section">
                                            <Label className="text-xs text-muted-foreground">BD Agent</Label>
                                            <Select
                                                value={selectedStudent.bd_agent_id || ''}
                                                onValueChange={async (newId) => {
                                                    try {
                                                        await apiClient.post(`/bd/students/${selectedStudent.id}/reassign?new_bd_agent_id=${newId}`);
                                                        const agent = bdAgentsList.find(a => a.id === newId);
                                                        setSelectedStudent(prev => ({ ...prev, bd_agent_id: newId, bd_agent_name: agent?.full_name }));
                                                        setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, bd_agent_id: newId, bd_agent_name: agent?.full_name } : s));
                                                        toast.success(`BD agent changed to ${agent?.full_name}`);
                                                    } catch { toast.error('Failed to change BD agent'); }
                                                }}
                                            >
                                                <SelectTrigger data-testid="bd-agent-select">
                                                    <SelectValue placeholder={selectedStudent.bd_agent_name || 'No BD assigned'} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {bdAgentsList.map(a => (
                                                        <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </TabsContent>

                                {/* NOTES TAB — Shared between CS, Mentor, BD for collaborative follow-ups */}
                                <TabsContent value="notes" className="mt-3 space-y-3" data-testid="mentor-tab-content-notes">
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <MentionTextarea value={newNote} onChange={setNewNote}
                                                placeholder="Add a session note. Use @name to mention a colleague (e.g. @falja)"
                                                rows={3} className="flex-1 text-sm" dataTestid="mentor-note-input"
                                                onSubmit={handleAddNote} />
                                            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || savingNote}
                                                className="self-end" data-testid="mentor-save-note-btn">
                                                {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Save</>}
                                            </Button>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground">
                                            Notes are shared with CS, Mentors, and BD. Use <span className="font-mono bg-pink-500/10 text-pink-700 dark:text-pink-300 px-1 rounded">@handle</span> to tag a colleague — they&apos;ll get an in-app notification.
                                        </p>
                                        <ScrollArea className="max-h-[320px]">
                                            <div className="space-y-2">
                                                {studentNotes.map(n => {
                                                    const ROLE_LABELS = {
                                                        cs_agent: 'CS', cs_head: 'CS Head', mentor: 'Mentor', master_of_academics: 'Mentor',
                                                        master_of_academics_: 'Mentor', business_development: 'BD', bd_executive: 'BD',
                                                        business_development_manager: 'BD Mgr',
                                                        super_admin: 'Admin', admin: 'Admin', sales_executive: 'Sales', team_leader: 'TL',
                                                    };
                                                    const role = (n.created_by_role || '');
                                                    const isMentor = role.includes('mentor') || role.includes('master_of_academics');
                                                    const isCS = role.includes('cs');
                                                    const isBD = role.includes('bd') || role.includes('business');
                                                    return (
                                                        <div key={n.id} data-note-id={n.id} className={`p-2.5 rounded text-sm border-l-4 transition-all ${
                                                            isMentor ? 'bg-violet-50 dark:bg-violet-950/30 border-l-violet-500'
                                                            : isCS ? 'bg-emerald-50 dark:bg-emerald-950/30 border-l-emerald-500'
                                                            : isBD ? 'bg-amber-50 dark:bg-amber-950/30 border-l-amber-500'
                                                            : 'bg-muted/50 border-l-gray-400'
                                                        }`}>
                                                            <RenderNoteBody text={n.text} />
                                                            <p className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-2">
                                                                <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-medium border bg-background">
                                                                    {ROLE_LABELS[role] || role || 'Agent'}
                                                                </span>
                                                                <span className="font-medium">{n.created_by_name}</span>
                                                                <span>{new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                                {studentNotes.length === 0 && (
                                                    <p className="text-xs text-muted-foreground py-4 text-center">No notes yet. Add the first session note above — CS, BD and other mentors will see it.</p>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    </div>
                                </TabsContent>

                                {/* ACTIVITY TIMELINE TAB */}
                                <TabsContent value="activity" className="mt-3" data-testid="mentor-tab-content-activity">
                                    {activityLoading ? (
                                        <div className="flex items-center justify-center py-6 text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
                                    ) : (
                                        <ScrollArea className="max-h-[300px]">
                                            <div className="relative pl-4 space-y-0">
                                                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                                                {activityTimeline.map((item, idx) => {
                                                    const isNote = item.type === 'note';
                                                    const isUpgrade = item.action?.includes('upgrade');
                                                    const isStage = item.action?.includes('stage') || item.action?.includes('updated');
                                                    const isBD = item.action?.includes('bd_') || item.action?.includes('redeposit');
                                                    const dotColor = isNote ? 'bg-blue-500' : isUpgrade ? 'bg-violet-500' : isBD ? 'bg-amber-500' : isStage ? 'bg-emerald-500' : 'bg-gray-400';
                                                    const role = ROLE_LABELS_M[item.user_role] || item.user_role || '';
                                                    return (
                                                        <div key={item.id || idx} className="relative flex gap-3 pb-3">
                                                            <div className={`absolute left-[-13px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-background ${dotColor}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs leading-relaxed">{item.description}</p>
                                                                <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                                                    {role && <span className="inline-flex px-1 py-0 rounded text-[9px] font-medium border bg-background">{role}</span>}
                                                                    <span className="font-medium">{item.user_name}</span>
                                                                    <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {activityTimeline.length === 0 && (
                                                    <p className="text-xs text-muted-foreground py-4 text-center">No activity recorded yet.</p>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    )}
                                </TabsContent>

                                {/* TRANSACTIONS TAB */}
                                <TabsContent value="transactions" className="mt-3" data-testid="mentor-tab-content-transactions">
                                    <TransactionHistory studentId={selectedStudent.id} isSuperAdmin={isSuperAdmin} />
                                </TabsContent>

                                {/* CALLS TAB */}
                                <TabsContent value="calls" className="mt-3 space-y-3" data-testid="mentor-tab-content-calls">
                                    {selectedStudent.call_recording_url && (
                                        <div className="p-3 bg-muted/50 rounded-lg border">
                                            <Label className="text-xs text-muted-foreground">Latest Recording</Label>
                                            <a href={selectedStudent.call_recording_url} target="_blank" rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-primary hover:underline mt-1">
                                                Play Recording
                                            </a>
                                        </div>
                                    )}
                                    <CallRecordings phone={selectedStudent.phone} />
                                    <CallHistory contactId={selectedStudent.id} />
                                </TabsContent>

                                {/* UPDATE TAB */}
                                <TabsContent value="update" className="mt-3 space-y-4" data-testid="mentor-tab-content-update">
                                    <div className="space-y-2">
                                        <Label>Mentorship Stage</Label>
                                        <Select value={updateData.mentor_stage} onValueChange={(value) => setUpdateData({ ...updateData, mentor_stage: value })}>
                                            <SelectTrigger data-testid="mentor-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                                            <SelectContent>
                                                {MENTOR_STAGES.map((stage) => (
                                                    <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Session Notes</Label>
                                        <Textarea value={updateData.notes} onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                                            placeholder="Add session notes, progress updates..." rows={3} data-testid="mentor-notes-input" />
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>Cancel</Button>
                                        <Button onClick={handleUpdateStudent} data-testid="update-mentor-btn">Update Status</Button>
                                    </DialogFooter>
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reminder Modal */}
            <ReminderModal
                open={showReminderModal}
                onClose={() => { setShowReminderModal(false); setReminderStudent(null); }}
                entityType="student"
                entityId={reminderStudent?.id}
                entityName={reminderStudent?.full_name}
                onSuccess={handleReminderSuccess}
            />

            {/* Monthly Closings Dialog - Net Revenue Breakdown */}
            <Dialog open={showClosingsDialog} onOpenChange={setShowClosingsDialog}>
                <DialogContent className="max-w-4xl max-h-[85vh]" data-testid="monthly-closings-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5 text-emerald-500" />
                                Monthly Closings — {monthlyClosings?.month || ''}
                            </span>
                            {monthlyClosings?.totals && (
                                <div className="flex items-center gap-4 text-sm">
                                    <span className="text-emerald-600 font-bold">Deposits: AED {Math.round(monthlyClosings.totals.deposits).toLocaleString()}</span>
                                    <span className="text-red-500 font-bold">Withdrawals: AED {Math.round(monthlyClosings.totals.withdrawals).toLocaleString()}</span>
                                    <Badge className="bg-emerald-600 text-white text-sm px-3 py-1">
                                        Net: AED {Math.round(monthlyClosings.totals.net_revenue).toLocaleString()}
                                    </Badge>
                                </div>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-[65vh]">
                        {loadingClosings ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
                            </div>
                        ) : monthlyClosings?.students?.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10 text-xs">Sr</TableHead>
                                        <TableHead className="text-xs">Name</TableHead>
                                        <TableHead className="text-xs">Email</TableHead>
                                        <TableHead className="text-xs">Mobile</TableHead>
                                        <TableHead className="text-xs text-right">Deposit (AED)</TableHead>
                                        <TableHead className="text-xs text-right">Withdrawal (AED)</TableHead>
                                        <TableHead className="text-xs text-right">Net (AED)</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {monthlyClosings.students.map((s, i) => (
                                        <TableRow key={s.student_id || i}>
                                            <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                                            <TableCell className="text-xs font-medium">{s.student_name}</TableCell>
                                            <TableCell className="text-xs text-muted-foreground">{s.student_email}</TableCell>
                                            <TableCell className="text-xs font-mono">{s.phone || '-'}</TableCell>
                                            <TableCell className="text-xs text-right font-mono text-emerald-600">
                                                {s.total_deposit > 0 ? Math.round(s.total_deposit).toLocaleString() : '-'}
                                            </TableCell>
                                            <TableCell className="text-xs text-right font-mono text-red-500">
                                                {s.total_withdrawal > 0 ? Math.round(s.total_withdrawal).toLocaleString() : '-'}
                                            </TableCell>
                                            <TableCell className="text-xs text-right font-mono font-bold">
                                                {Math.round(s.total_deposit - s.total_withdrawal).toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow className="bg-muted/50 font-bold">
                                        <TableCell colSpan={4} className="text-xs text-right">Total</TableCell>
                                        <TableCell className="text-xs text-right font-mono text-emerald-600">
                                            {Math.round(monthlyClosings.totals.deposits).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-xs text-right font-mono text-red-500">
                                            {Math.round(monthlyClosings.totals.withdrawals).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-xs text-right font-mono">
                                            {Math.round(monthlyClosings.totals.net_revenue).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                                No closings found for this month
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MentorCRMPage;
